import { Link, useParams } from '@tanstack/react-router'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/shadcn/hover-card'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import { Minus, Plus, Server as ServerIcon, Settings2, X } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/shadcn/popover'
import type { Container, ContainerCreate, ContainerList, ContainerPatch, ServerCiJobs, EnrolCode, Server, ServerCheck, ServerCreate, ServerList, ServerMemory } from '@one-deployment/api'
import { Button, Card, Dialog, Input, Label, Meter, PageHeader, Segmented, Select, SkeletonRows, Table, Td, Tr } from '@/components/ui'
import { Button as SButton } from '@/components/shadcn/button'
import { Age, EmptyState, ErrorState } from '@/components/status'
import { RoleGate } from '@/components/RoleGate'
import { ask } from '@/components/ask'
import { useProjects } from '@/features/shell/useOverview'
import { enc, get, patch, post } from '@/lib/api'
import { cn } from '@/lib/cn'
import { dateTime, gb } from '@/lib/format'
import { useGlobalQueryOpts } from '@/lib/live'
import { qk } from '@/lib/query'

const STATE: Record<Server['state'], string> = { active: 'text-ok', draining: 'text-warn', down: 'text-err', retired: 'text-fg-faint' }

/** Servers and containers ride the global topic; the poll is the fallback. */
/** The CPU bar: real busy share when the box sends it. The load average is the
 * fallback for older boxes; it counts processes waiting on disk and trails off,
 * so an idle CI server read red. */
export function cpuUsage(st: { load: number; cpuPct?: number | null }, cores: number | null): { pct: number | null; text: string } {
  if (typeof st.cpuPct === 'number') {
    const busy = cores ? ` · ${((st.cpuPct / 100) * cores).toFixed(1)} of ${cores} cores` : ''
    return { pct: st.cpuPct, text: `${Math.round(st.cpuPct)}%${busy}` }
  }
  return { pct: cores ? (st.load / cores) * 100 : null, text: `${st.load.toFixed(2)} / ${cores ?? '—'} cores` }
}

function useServers() {
  return useQuery({ queryKey: qk.servers(), queryFn: ({ signal }) => get<ServerList>('/api/servers', signal), ...useGlobalQueryOpts(30_000) })
}
function useContainers() {
  return useQuery({ queryKey: qk.containers(), queryFn: ({ signal }) => get<ContainerList>('/api/containers', signal), ...useGlobalQueryOpts(30_000) })
}

/** The one-use code a box types in at first boot. Shown once: it is never stored in the clear, so closing this loses it. */
function EnrolCodeView({ code }: { code: EnrolCode }) {
  const [copied, setCopied] = useState(false)
  return (
    <div className="space-y-2 text-sm">
      <div className="flex items-center gap-2">
        <code className="flex-1 rounded-sm bg-surface-2 px-2 py-1.5 font-mono text-base tracking-wider">{code.enrol_code}</code>
        <Button onClick={() => void navigator.clipboard.writeText(code.enrol_code).then(() => setCopied(true))}>{copied ? 'Copied' : 'Copy'}</Button>
      </div>
      <p className="text-xs text-fg-subtle">Valid until {dateTime(code.enrol_expires_at)} (24 h), and only once. Type it into the box at first boot. It is not shown again — mint a new one if it is lost.</p>
    </div>
  )
}

/** The next free `box-N` and its hostname on the fleet's domain: servers are named, not typed in. */
export function nextServer(servers: Server[]): ServerCreate {
  const n = Math.max(0, ...servers.flatMap((x) => [x.id, x.hostname]).map((v) => Number(/^box-(\d+)/.exec(v)?.[1] ?? 0))) + 1
  const domain = servers.map((x) => x.hostname.split('.').slice(1).join('.')).find(Boolean) ?? 'trisof.com'
  return { id: `box-${n}`, hostname: `box-${n}.${domain}` }
}

export function AddServer() {
  const [open, setOpen] = useState(false)
  const servers = useServers().data?.servers ?? []
  const [form, setForm] = useState<ServerCreate | null>(null)
  const [editing, setEditing] = useState(false)
  const qc = useQueryClient()
  const create = useMutation({
    mutationFn: (b: ServerCreate) => post<EnrolCode>('/api/servers', b),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.servers() }),
  })
  const f = form ?? nextServer(servers)
  const close = (o: boolean) => { setOpen(o); if (!o) { create.reset(); setForm(null); setEditing(false) } }
  return (
    <RoleGate action="servers.manage">
      <SButton variant="outline" size="xs" className="h-6 gap-1 text-xs" onClick={() => setOpen(true)}><Plus size={12} />Add server</SButton>
      <Dialog open={open} onOpenChange={close} title="Add server" description="Registers the box and mints its one-use enrolment code.">
        {create.data ? <EnrolCodeView code={create.data} /> : (
          <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); create.mutate(f) }}>
            {editing ? <>
              <div><Label htmlFor="srv-id">Id</Label><Input id="srv-id" value={f.id} pattern="[a-z0-9][a-z0-9-]{0,62}" required onChange={(e) => setForm({ ...f, id: e.target.value })} /></div>
              <div><Label htmlFor="srv-host">Hostname</Label><Input id="srv-host" value={f.hostname} required onChange={(e) => setForm({ ...f, hostname: e.target.value })} /></div>
            </> : (
              <div className="flex items-center gap-2 rounded-md border border-border bg-surface-2 px-3 py-2 text-sm">
                <span className="font-medium">{f.id}</span><span className="text-fg-subtle">{f.hostname}</span>
                <SButton type="button" variant="link" size="xs" className="ml-auto h-auto p-0 text-xs" onClick={() => setEditing(true)}>Change</SButton>
              </div>
            )}
            <p className="text-xs text-fg-subtle">Its cores, memory and disk are read from the box when its agent first checks in.</p>
            {create.error && <ErrorState error={create.error} compact />}
            <div className="flex justify-end"><Button type="submit" variant="primary" disabled={create.isPending}>Create</Button></div>
          </form>
        )}
      </Dialog>
    </RoleGate>
  )
}

function NewEnrolCode({ s }: { s: Server }) {
  const [open, setOpen] = useState(false)
  const mint = useMutation({ mutationFn: () => post<EnrolCode>(`/api/servers/${enc(s.id)}/enrol-code`) })
  return (
    <RoleGate action="servers.manage">
      <Button disabled={s.state === 'retired' || mint.isPending} title="For a rebuilt box or a credential to rotate; the old secret works until the code is used"
        onClick={() => { setOpen(true); mint.mutate() }}>New enrol code</Button>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) mint.reset() }} title={`Enrol code for ${s.hostname}`}>
        {mint.data ? <EnrolCodeView code={mint.data} /> : mint.error ? <ErrorState error={mint.error} compact /> : <SkeletonRows rows={1} />}
      </Dialog>
    </RoleGate>
  )
}

const CHECK: Record<ServerCheck['status'], string> = { pass: 'text-ok', warn: 'text-warn', fail: 'text-err' }
const STEP: Record<ServerMemory['learned'][number]['step'], string> = { install: 'Install', baseline: 'Baseline suite', suite: 'Suite', dev_server: 'Preview server', browser_proof: 'Browser test' }
const mb = (n: number) => gb(n / 1024)

/** Memory admission per container: the cap, what is reserved, and what the box has learned each step needs. */
function MemoryPanel({ memory }: { memory: ServerMemory[] }) {
  if (memory.length === 0) return <EmptyState title="No memory report yet" body="It arrives with the box's next heartbeat." />
  return (
    <div className="divide-y divide-border">
      {memory.map((m) => (
        <div key={m.instance} className="space-y-2 p-3 text-xs">
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <span className="font-mono">{m.instance}</span>
            <span className="flex items-center gap-2 text-fg-muted">used <Meter value={m.capMb ? (m.usedMb / m.capMb) * 100 : null} /> {mb(m.usedMb)} of {mb(m.capMb)}</span>
            <span className="text-fg-muted">reserved {mb(m.reservedMb)}</span>
            {m.queued > 0 && <span className="text-warn">{m.queued} step(s) waiting for room</span>}
          </div>
          {m.running.length > 0 && (
            <ul className="space-y-0.5 text-fg-muted">
              {m.running.map((r) => (
                <li key={`${r.jobId}-${r.step}`}>
                  {STEP[r.step]} · {mb(r.usedMb)} of {mb(r.reservedMb)}{' '}
                  {r.capped ? <span className="text-fg-subtle">capped</span> : <span className="text-warn" title="Accounted, not enforced: step cgroups are unavailable in this container">uncapped</span>}
                </li>
              ))}
            </ul>
          )}
          {m.learned.length > 0 ? (
            <Table head={['Step', 'Learned peak', 'Next reserves', 'Runs']}>
              {m.learned.map((l) => (
                <Tr key={l.step}><Td>{STEP[l.step]}</Td><Td className="tabular-nums">{mb(l.peakMb)}</Td><Td className="tabular-nums">{mb(l.needMb)}</Td><Td className="tabular-nums">{l.runs}</Td></Tr>
              ))}
            </Table>
          ) : <p className="text-fg-subtle">Nothing measured yet: each step starts at the project's starting memory.</p>}
        </div>
      ))}
    </div>
  )
}

/** What `trisof-box doctor` found, as the box last reported it. */
function ChecksPanel({ checks }: { checks: Server['checks'] }) {
  if (!checks) return <EmptyState title="No checks reported yet" body="The box runs them when its agent starts, and every six hours." />
  return (
    <div>
      <ul className="divide-y divide-border">
        {checks.items.map((c, i) => (
          <li key={`${c.id}-${i}`} className="flex gap-3 px-3 py-1.5 text-xs">
            <span className={cn('w-10 shrink-0 font-medium capitalize', CHECK[c.status])}>{c.status}</span>
            <span className="w-28 shrink-0 font-mono text-fg-subtle">{c.id}</span>
            <span className="flex-1">{c.detail}{c.fix && <span className="block text-fg-subtle">Fix: {c.fix}</span>}</span>
          </li>
        ))}
      </ul>
      <p className="px-3 py-2 text-xs text-fg-subtle">Checked {dateTime(checks.at)}. Run <code>trisof-box doctor</code> on the box for a fresh look.</p>
    </div>
  )
}

/**
 * Used = total − free, but only when the total is a real total: boxes before
 * 0.1.127 sent their free figures at connect as "capacity": used read near zero or
 * negative. For those the card shows free only.
 */
const usedOf = (total: number | null | undefined, free: number) => (total && total >= free ? total - free : null)
/** 0.1.127 is the first agent whose capacity is a real total. */
export const reportsTotals = (v: string | null | undefined) => {
  const [a = 0, b = 0, c = 0] = (v ?? '').split('.').map(Number)
  return a > 0 || b > 1 || (b === 1 && c >= 127)
}
const memTotal = (s: Server) => (reportsTotals(s.agentVersion) ? s.memMb : null)
const diskTotal = (s: Server) => (reportsTotals(s.agentVersion) ? s.diskGb : null)
const memUsedPct = (s: Server) => { const t = memTotal(s); const u = s.stats ? usedOf(t, s.stats.memFreeMb) : null; return u != null && t ? (u / t) * 100 : null }
const CSTATE: Record<Container['state'], string> = { ready: 'text-ok', starting: 'text-warn', draining: 'text-warn', stopped: 'text-fg-faint', failed: 'text-err' }
const SIZES = [2048, 4096, 8192, 16384]

function invalidate(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: qk.servers() })
  void qc.invalidateQueries({ queryKey: qk.containers() })
}

/** "General", or "General 2" when the project has more than one container. */
export function containerName(c: Container, all: Container[], projectName: string | undefined): string {
  // The sandbox project's full name repeats the platform's; on a server card it is just the sandbox.
  const name = c.projectKey === 'sandbox' ? 'Sandbox' : projectName ?? c.projectKey.replace(/[-_]+/g, ' ').replace(/^./, (x) => x.toUpperCase())
  if (all.filter((x) => x.projectKey === c.projectKey && x.state !== 'stopped').length < 2) return name
  return `${name} ${c.id.slice(c.projectKey.length + 1)}`
}

/**
 * A doctor check about one container names it as its second part
 * (`worktree_kept:rhythm-1:TC-3719`): it belongs on that container's box,
 * not on the server, which it says nothing about.
 */
export const checkContainer = (id: string, containers: string[]) => containers.find((c) => id.split(':')[1] === c) ?? null

/** The failing checks as a list: what each says and what to do. */
function FailingChecks({ items }: { items: ServerCheck[] }) {
  return (
    <ul className="max-h-[60vh] divide-y overflow-y-auto text-xs">
      {items.map((c, i) => (
        <li key={i} className="flex flex-col gap-0.5 px-3 py-2">
          <span className="font-mono text-err">{c.id}</span>
          <span className="text-fg">{c.detail}</span>
          {c.fix && <span className="text-fg-subtle">Fix: {c.fix}</span>}
        </li>
      ))}
    </ul>
  )
}

const CDOT: Record<Container['state'], string> = { ready: 'bg-ok', starting: 'bg-warn', draining: 'bg-warn', stopped: 'bg-fg-faint', failed: 'bg-err' }

/** One container as a box inside its server's card. Delete is refused by the API while sessions run or if it is the project's last. */
function ContainerBox({ c, name, mem, fails }: { c: Container; name: string; mem: ServerMemory | undefined; fails: ServerCheck[] }) {
  const qc = useQueryClient()
  const del = useMutation({ mutationFn: () => post(`/api/containers/${enc(c.id)}/drain`), onSettled: () => invalidate(qc) })
  const gone = c.state === 'draining' || c.state === 'stopped'
  // The container's own limit: capMb is the box's admission budget, which can be
  // the whole server's and made one container read "2.6 of 12 GB".
  const cap = c.memMb
  return (
    <div className="relative flex w-48 flex-col gap-1.5 rounded-md border border-border bg-surface-2 p-2 text-xs">
      <Link to="/p/$project" params={{ project: c.projectKey }} className={cn("flex items-center gap-1.5 font-medium hover:underline", fails.length ? "pr-20" : "pr-12")} title={`${c.id} · ${c.state}`}>
        <span className={cn('size-2 shrink-0 rounded-full', CDOT[c.state])} aria-label={c.state} />
        <span className="truncate">{name}</span>
      </Link>
      <div className="flex justify-between text-fg-muted"><span>Sessions</span><span className="tabular-nums">{c.sessions} of {c.slots}</span></div>
      <div className="flex flex-col gap-1">
        <div className="flex justify-between text-fg-muted"><span>RAM</span><span className="tabular-nums">{mem ? `${mb(mem.usedMb)} of ${mb(cap)}` : mb(cap)}</span></div>
        <Meter value={mem ? (mem.usedMb / Math.max(cap, 1)) * 100 : null} />
      </div>
      <div className="absolute top-1 right-7 flex items-center gap-0.5">
        {fails.length > 0 && (
          <Popover>
            <PopoverTrigger asChild>
              <button type="button" className="flex h-5 items-center gap-1 rounded-sm px-1 text-[11px] tabular-nums text-err hover:bg-hover" title="Failing checks" aria-label={`${fails.length} failing checks`}>
                <span className="size-1.5 rounded-full bg-err" />{fails.length}
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-[28rem] p-0"><FailingChecks items={fails} /></PopoverContent>
          </Popover>
        )}
        {!gone && <RoleGate action="servers.manage"><ContainerSettings c={c} /></RoleGate>}
      </div>
      {!gone && (
        <RoleGate action="servers.manage">
          <SButton variant="ghost" size="icon-xs" className="absolute top-1 right-1 size-5 rounded-sm text-fg-subtle hover:bg-hover hover:text-err" title="Delete container" aria-label="Delete container" disabled={del.isPending}
            onClick={() => void ask({ title: `Delete ${name}?`, body: 'It takes no new work and is removed from the server. The project keeps its other containers.', confirm: 'Delete', danger: true }).then((ok) => ok && del.mutate())}>
            <X size={12} />
          </SButton>
        </RoleGate>
      )}
      {del.error && <ErrorState error={del.error} compact />}
    </div>
  )
}

/** A container's settings: memory (restarts it) and how many sessions it takes at once. */
function ContainerSettings({ c }: { c: Container }) {
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [memMb, setMem] = useState(c.memMb)
  const [slots, setSlots] = useState(c.slots)
  const save = useMutation({
    mutationFn: (body: ContainerPatch) => patch(`/api/containers/${enc(c.id)}`, body, c.version ?? 0),
    onSuccess: () => setOpen(false),
    onSettled: () => invalidate(qc),
  })
  const sizes = SIZES.includes(c.memMb) ? SIZES : [...SIZES, c.memMb].sort((a, b) => a - b)
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) { setMem(c.memMb); setSlots(c.slots); save.reset() } }}>
      <PopoverTrigger asChild>
        <SButton variant="ghost" size="icon-xs" className="size-5 rounded-sm text-fg-subtle hover:bg-hover hover:text-fg" title="Container settings" aria-label="Container settings"><Settings2 size={12} /></SButton>
      </PopoverTrigger>
      <PopoverContent align="end" className="flex w-64 flex-col gap-3 p-3 text-xs">
        <div className="font-medium">{c.id}</div>
        <div><Label htmlFor={`mem-${c.id}`}>Memory</Label>
          <Select id={`mem-${c.id}`} value={memMb} onChange={(e) => setMem(Number(e.target.value))}>
            {sizes.map((m) => <option key={m} value={m}>{mb(m)}</option>)}
          </Select>
          {memMb !== c.memMb && <p className="mt-1 text-warn">Restarts the container. Refused while a session is open.</p>}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-fg-muted">Sessions at once</span>
          <Button size="sm" disabled={slots <= 1} onClick={() => setSlots(slots - 1)}><Minus size={12} /></Button>
          <span className="w-5 text-center tabular-nums">{slots}</span>
          <Button size="sm" disabled={slots >= 16} onClick={() => setSlots(slots + 1)}><Plus size={12} /></Button>
        </div>
        {save.error && <ErrorState error={save.error} compact />}
        <Button size="sm" disabled={save.isPending || (memMb === c.memMb && slots === c.slots)}
          onClick={() => save.mutate({ ...(memMb !== c.memMb ? { memMb } : {}), ...(slots !== c.slots ? { slots } : {}) })}>Save</Button>
      </PopoverContent>
    </Popover>
  )
}

/** The + on a project server: pick a project and a size. */
function AddContainer({ s }: { s: Server }) {
  const [open, setOpen] = useState(false)
  const projects = useProjects().data?.projects ?? []
  const [form, setForm] = useState<{ projectKey: string; memMb: number }>({ projectKey: '', memMb: 4096 })
  const qc = useQueryClient()
  const create = useMutation({
    mutationFn: () => post<Container>('/api/containers', { projectKey: form.projectKey, serverId: s.id, memMb: form.memMb } satisfies ContainerCreate),
    onSuccess: () => { invalidate(qc); setOpen(false) },
  })
  return (
    <RoleGate action="servers.manage">
      <button type="button" disabled={s.state !== 'active'} title="Add container"
        aria-label="Add container" className="flex min-h-24 w-48 items-center justify-center rounded-md border border-dashed border-border text-fg-subtle outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 hover:border-border-strong hover:text-fg disabled:opacity-50"
        onClick={() => setOpen(true)}><Plus size={16} /></button>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) create.reset() }} title={`Add container on ${s.id}`}>
        <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); create.mutate() }}>
          <div><Label htmlFor="ct-proj">Project</Label>
            <Select id="ct-proj" required value={form.projectKey} onChange={(e) => setForm({ ...form, projectKey: e.target.value })}>
              <option value="">Pick a project</option>
              {projects.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
            </Select></div>
          <div><Label htmlFor="ct-mem">Memory</Label>
            <Select id="ct-mem" value={form.memMb} onChange={(e) => setForm({ ...form, memMb: Number(e.target.value) })}>
              {SIZES.map((m) => <option key={m} value={m}>{mb(m)}</option>)}
            </Select>
            {s.stats && <p className="mt-1 text-xs text-fg-subtle">{mb(s.stats.memFreeMb)} free on {s.id}</p>}</div>
          {create.error && <ErrorState error={create.error} compact />}
          <div className="flex justify-end"><Button type="submit" variant="primary" disabled={create.isPending || !form.projectKey}>Create</Button></div>
        </form>
      </Dialog>
    </RoleGate>
  )
}

/** CI servers run pipeline jobs, not containers: + / − is how many at once. */
function CiSlots({ s }: { s: Server }) {
  const qc = useQueryClient()
  const set = useMutation({ mutationFn: (n: number) => patch(`/api/servers/${enc(s.id)}`, { ciSlots: n }, s.version), onSettled: () => invalidate(qc) })
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-fg-muted">Pipelines at once</span>
      <RoleGate action="servers.manage">
        <Button size="sm" disabled={set.isPending || s.ciSlots <= 0} onClick={() => set.mutate(s.ciSlots - 1)}><Minus size={12} /></Button>
      </RoleGate>
      <span className="w-6 text-center font-medium tabular-nums">{s.ciSlots}</span>
      <RoleGate action="servers.manage">
        <Button size="sm" disabled={set.isPending || s.ciSlots >= 32} onClick={() => set.mutate(s.ciSlots + 1)}><Plus size={12} /></Button>
      </RoleGate>
      {set.error && <ErrorState error={set.error} compact />}
      <CiJobs s={s} />
    </div>
  )
}

/** What the CI server is running now; each opens its pipeline. */
function CiJobs({ s }: { s: Server }) {
  const q = useQuery({ queryKey: ['servers', s.id, 'ci-jobs'], queryFn: () => get<ServerCiJobs>(`/api/servers/${enc(s.id)}/ci-jobs`), refetchInterval: 10_000 })
  const jobs = q.data?.jobs ?? []
  if (!jobs.length) return <span className="ml-3 text-fg-subtle">{q.data ? 'Nothing running' : ''}</span>
  return (
    <div className="ml-3 flex flex-wrap items-center gap-1.5">
      {jobs.map((j) => (
        <Link key={j.id} to="/p/$project/ci/$runId" params={{ project: j.projectKey, runId: j.runId }}
          className="flex items-center gap-1.5 rounded-md border border-border bg-surface-2 px-2 py-1 hover:border-ring/60" title={`${j.projectKey} · ${j.branch ?? ''} · started ${j.startedAt ? dateTime(j.startedAt) : '—'}`}>
          <span className="size-1.5 animate-pulse rounded-full bg-warn" />
          <span className="text-fg-muted">{j.projectKey}</span><span className="font-medium">{j.name}</span>
        </Link>
      ))}
    </div>
  )
}

/** One resource on a server's header: label, bar, figure. */
function Usage({ label, pct, text }: { label: string; pct: number | null; text: string }) {
  return (
    <span className="flex items-center gap-2 text-xs text-fg-muted" title={text}>
      {label}<Meter value={pct} /><span className="tabular-nums text-fg-subtle">{text}</span>
    </span>
  )
}

/** The Disk figure; a click lists what fills it, biggest first. */
function DiskUsage({ s, children }: { s: Server; children: ReactNode }) {
  const d = s.disk
  if (!d?.parts.length) return <>{children}</>
  const top = d.parts[0]!.gb || 1
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="cursor-pointer rounded hover:bg-hover" title="What fills the disk">{children}</button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-3">
        <div className="mb-2 flex items-baseline justify-between text-sm">
          <span className="font-medium">Disk</span>
          <span className="text-xs text-fg-subtle">{gb(d.usedGb)} of {gb(d.totalGb)} · <Age from={d.at} /></span>
        </div>
        <ul className="space-y-1.5">
          {d.parts.map((p) => (
            <li key={p.label} className="text-xs">
              <div className="flex justify-between gap-3"><span className="truncate text-fg-muted">{p.label}</span><span className="tabular-nums text-fg">{gb(p.gb)}</span></div>
              <div className="mt-0.5 h-1 rounded bg-surface-3"><div className="h-1 rounded bg-info" style={{ width: `${Math.max(2, (p.gb / top) * 100)}%` }} /></div>
            </li>
          ))}
        </ul>
        <p className="mt-2 text-[11px] text-fg-faint">Measured every 30 minutes. Idle worktrees with nothing unpushed, unused volumes and old images are cleaned daily, and hourly when over 80% full.</p>
      </PopoverContent>
    </Popover>
  )
}

const DOT: Record<Server['state'], string> = { active: 'bg-ok', draining: 'bg-warn', down: 'bg-err', retired: 'bg-fg-faint' }

function ServerCard({ s, containers, all, names }: { s: Server; containers: Container[]; all: Container[]; names: Map<string, string> }) {
  const ramPct = memUsedPct(s)
  const up = s.state !== 'down' && s.stats
  const diskUsed = s.stats ? usedOf(diskTotal(s), s.stats.diskFreeGb) : null
  const memUsed = s.stats ? usedOf(memTotal(s), s.stats.memFreeMb) : null
  const fails = s.checks?.items.filter((c) => c.status === 'fail') ?? []
  const ids = containers.map((c) => c.id)
  const serverFails = fails.filter((f) => !checkContainer(f.id, ids))
  return (
    <div className="rounded-lg border border-border bg-surface-2">
      <div className="flex min-h-9 flex-wrap items-center gap-x-5 gap-y-1 border-b border-border px-3 py-1.5 text-sm">
        <span className="flex items-center gap-2">
          <span className={cn('size-2 rounded-full', DOT[s.state])} aria-label={s.state} title={`${s.state} · last seen ${s.lastSeenAt ? dateTime(s.lastSeenAt) : 'never'}`} />
          <Link to="/servers/$server" params={{ server: s.id }} className="font-medium hover:underline" title={s.hostname}>{s.id}</Link>
        </span>
        {up && <>
          <Usage label="CPU" {...cpuUsage(s.stats!, s.cpuCores)} />
          <Usage label="RAM" pct={ramPct} text={memUsed != null ? `${gb(memUsed / 1024)} of ${gb(s.memMb! / 1024)}` : `${gb(s.stats!.memFreeMb / 1024)} free`} />
          <DiskUsage s={s}><Usage label="Disk" pct={diskUsed != null && s.diskGb ? (diskUsed / s.diskGb) * 100 : null} text={diskUsed != null ? `${gb(diskUsed)} of ${gb(s.diskGb)}` : `${gb(s.stats!.diskFreeGb)} free`} /></DiskUsage>
        </>}
        {serverFails.length > 0 && (
          // Which checks, and what to do about each: "checks failing" alone left nothing to act on.
          <HoverCard openDelay={150}>
            <HoverCardTrigger asChild>
              <button type="button" className="cursor-help text-xs text-err underline decoration-dotted underline-offset-2">
                {serverFails.length} check{serverFails.length === 1 ? '' : 's'} failing
              </button>
            </HoverCardTrigger>
            <HoverCardContent align="start" className="w-[28rem] p-0"><FailingChecks items={serverFails} /></HoverCardContent>
          </HoverCard>
        )}
        <span className="ml-auto text-xs text-fg-subtle" title="Agent version">v{s.agentVersion ?? '—'}</span>
      </div>
      <div className="flex flex-wrap gap-2 p-3">
        {s.role === 'ci' ? <CiSlots s={s} /> : (
          <>
            {containers.map((c) => <ContainerBox key={c.id} c={c} name={containerName(c, all, names.get(c.projectKey))}
              mem={s.memory.find((m) => m.instance === c.id) ?? s.memory.find((m) => m.project === c.projectKey)}
              fails={fails.filter((f) => checkContainer(f.id, ids) === c.id)} />)}
            <AddContainer s={s} />
          </>
        )}
      </div>
    </div>
  )
}

export function ServersPage() {
  const { data, error, refetch } = useServers()
  const containers = useContainers().data?.containers ?? []
  const names = new Map((useProjects().data?.projects ?? []).map((p) => [p.key, p.name]))
  const servers = data?.servers
  return (
    <div className="space-y-3 px-6 py-5">
      {error && !servers ? <ErrorState error={error} retry={() => void refetch()} /> : !servers ? <SkeletonRows rows={4} height={64} /> : servers.length === 0 ? (
        <EmptyState icon={<ServerIcon size={20} />} title="No servers registered" body="Provision one in infra/, then Add server (top right) for its enrol code. It appears once its agent checks in." />
      ) : servers.map((s) => <ServerCard key={s.id} s={s} all={containers} names={names} containers={containers.filter((c) => c.serverId === s.id && c.state !== 'stopped')} />)}
    </div>
  )
}

export function ServerPage() {
  const { server } = useParams({ strict: false }) as { server: string }
  const [tab, setTab] = useState<'containers' | 'health'>('containers')
  const qc = useQueryClient()
  const servers = useServers()
  const containersQ = useContainers()
  const s = servers.data?.servers.find((x) => x.id === server)
  const containers = (containersQ.data?.containers ?? []).filter((c) => c.serverId === server)
  const act = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: qk.servers() })
      void qc.invalidateQueries({ queryKey: qk.containers() })
    },
  })

  if (servers.error && !servers.data) return <ErrorState error={servers.error} retry={() => void servers.refetch()} />
  if (!servers.data) return <div className="p-5"><SkeletonRows rows={6} /></div>
  if (!s) return <EmptyState title="No such server" body="It may have been retired. Go back to Servers." action={<Link to="/servers"><Button>Servers</Button></Link>} />

  const draining = s.state === 'draining'
  return (
    <div>
      <PageHeader
        title={s.hostname}
        meta={<><span className={STATE[s.state]}>{s.state}</span> · agent {s.agentVersion ?? '—'} · claude {s.claudeVersion ?? '—'}</>}
        actions={
          <div className="flex gap-1.5">
          <NewEnrolCode s={s} />
          <RoleGate action="servers.manage">
            <Button disabled={act.isPending || s.state === 'down' || s.state === 'retired'}
              onClick={() => act.mutate(() => patch(`/api/servers/${enc(s.id)}`, { state: draining ? 'active' : 'draining' }, s.version))}>
              {draining ? 'Undrain server' : 'Drain server'}
            </Button>
          </RoleGate>
          </div>
        }
      >
        <div className="pb-3">
          <Segmented value={tab} onChange={setTab} options={[{ value: 'containers', label: 'Containers' }, { value: 'health', label: 'Health' }]} />
        </div>
      </PageHeader>
      {act.error && <div className="px-5 pt-3"><ErrorState error={act.error} compact /></div>}
      <div className="p-5">
        {tab === 'containers' && (
          <Card>
            {containers.length === 0 ? <EmptyState title="No containers on this server" body="Containers are placed here when a project needs capacity." /> : (
              <Table head={['Id', 'Project', 'Image', 'Memory', 'Sessions', 'State', '']}>
                {containers.map((c) => (
                  <Tr key={c.id}>
                    <Td mono>{c.id}</Td>
                    <Td>{c.projectKey}</Td>
                    <Td mono className="text-fg-subtle">{c.image}</Td>
                    <Td className="tabular-nums">{gb(c.memMb / 1024)}</Td>
                    <Td className="tabular-nums">{c.busy}/{c.slots}</Td>
                    <Td className={c.state === 'ready' ? 'text-ok' : c.state === 'failed' ? 'text-err' : 'text-warn'}>{c.state}</Td>
                    <Td className="flex gap-1">
                      <RoleGate action="container.restart" project={c.projectKey}>
                        <Button size="sm" disabled={act.isPending || c.state === 'draining' || c.state === 'stopped'}
                          title="Let running sessions finish, then recreate the container. Shift-click to restart now; interrupted work is retried without using an attempt."
                          onClick={(e) => act.mutate(() => post(`/api/containers/${enc(c.id)}/restart`, { force: e.shiftKey }))}>Restart</Button>
                      </RoleGate>
                      <RoleGate action="servers.manage">
                        <Button size="sm" disabled={act.isPending || c.state === 'draining' || c.state === 'stopped'}
                          title="Finish the sessions running in it, take no new work, then stop"
                          onClick={() => act.mutate(() => post(`/api/containers/${enc(c.id)}/drain`))}>Drain</Button>
                      </RoleGate>
                    </Td>
                  </Tr>
                ))}
              </Table>
            )}
            <p className="px-3 py-2 text-xs text-fg-subtle">A drained container is replaced when its project next needs capacity.</p>
          </Card>
        )}
        {tab === 'containers' && (
          <Card className="mt-3">
            <div className="border-b border-border px-3 py-2 text-sm font-medium">Memory</div>
            <MemoryPanel memory={s.memory} />
          </Card>
        )}
        {tab === 'health' && (
          <Card>
            <div className="grid grid-cols-2 gap-3 p-3 text-sm">
              <div>State: <span className={STATE[s.state]}>{s.state}</span></div>
              <div>Last seen: <Age from={s.lastSeenAt} /> ago</div>
              <div>Last health report: {s.lastHealthAt ? dateTime(s.lastHealthAt) : '—'}</div>
              <div>Load: {s.stats ? s.stats.load.toFixed(2) : '—'}{s.cpuCores != null && ` on ${s.cpuCores} cores`}</div>
              <div>Memory free: {s.stats ? gb(s.stats.memFreeMb / 1024) : '—'}{s.memMb != null && ` of ${gb(s.memMb / 1024)}`}</div>
              <div>Disk free: {gb(s.stats?.diskFreeGb)}{s.diskGb != null && ` of ${gb(s.diskGb)}`}</div>
            </div>
            <p className="px-3 pb-3 text-xs text-fg-subtle">Patching and rolling reboots run from CI (Ansible).</p>
          </Card>
        )}
        {tab === 'health' && (
          <Card className="mt-3">
            <div className="border-b border-border px-3 py-2 text-sm font-medium">Checks</div>
            <ChecksPanel checks={s.checks} />
          </Card>
        )}
      </div>
    </div>
  )
}
